import * as React from 'react';
import {
    Button,
    TextInput,
    FormGroup,
    Form,
    Spinner
} from '@patternfly/react-core';
import {useState, useEffect, useRef, useCallback} from "react";
import {createCampaign, createTargetReachCampaign, getOrganizations, getAdsByOrganizationId} from "@app/utils/utils";
import {
    BuildingIcon,
    CheckCircleIcon,
    CubesIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    MapMarkerIcon,
    UsersIcon,
    RouteIcon,
    SearchIcon,
    CrosshairsIcon,
    CheckIcon
} from "@patternfly/react-icons";
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { PURPLE_GRADIENT, colors, radii } from '@app/components/designTokens';
import {
    GoogleMap,
    LoadScript,
    Marker,
    Autocomplete
} from '@react-google-maps/api';
import { GeoFenceMapModal } from './GeoFenceMapModal';
import { DrawRouteMapModal } from './DrawRouteMapModal';
import { GeoFenceLocation } from './types/geoFence';
import { DrawRoutesConfig } from './types/drawRoutes';
import { SOCAL_RESTRICTION, SOCAL_AUTOCOMPLETE_BOUNDS, isWithinSoCalBounds } from './types/mapConstants';
import { ScheduleRunsStep } from './ScheduleRunsStep';
import { creditCostForType } from './utils';

// Google Maps config
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const libraries: ("places")[] = ["places"];

export interface ICreateCampaignProps {
    triggerRefresh: Function;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    hideButton?: boolean;
    initialCampaignType?: string;
    existingCampaigns?: any[];
    onCampaignCreated?: (campaignId: number) => void;
}

const CreateCampaign: React.FunctionComponent<ICreateCampaignProps> = (props) => {

    const [internalModalOpen, setInternalModalOpen] = React.useState(false);
    const [name, setName] = useState('');
    const [type, setType] = useState<string>('');
    const nameInputRef = React.useRef<HTMLInputElement>(null);
    const [selectedAdIds, setSelectedAdIds] = useState<string[]>([]);
    const [org_id, setOrgId] = useState('');
    const [currentStep, setCurrentStep] = useState(1);
    const [organizations, setOrganizations] = useState<any[]>([]);
    const [ads, setAds] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [geoFenceLocation, setGeoFenceLocation] = useState<GeoFenceLocation | null>(null);
    const [showGeoFenceModal, setShowGeoFenceModal] = useState(false);
    const [drawRoutesConfig, setDrawRoutesConfig] = useState<DrawRoutesConfig | null>(null);
    const [showDrawRouteModal, setShowDrawRouteModal] = useState(false);
    const [error, setError] = useState<React.ReactNode | null>(null);
    // Slot-booking step needs the created campaign id (DRAFT created on review-step Next)
    const [createdCampaignId, setCreatedCampaignId] = useState<number | null>(null);

    // Target Reach state
    const [targetReachUserPrompt, setTargetReachUserPrompt] = useState('');
    const [targetReachStartLat, setTargetReachStartLat] = useState('32.7157');
    const [targetReachStartLng, setTargetReachStartLng] = useState('-117.1611');
    const [targetReachMaxHours, setTargetReachMaxHours] = useState('4');
    const [targetReachBudgetDays, setTargetReachBudgetDays] = useState('14');
    const [targetReachRoadPref, setTargetReachRoadPref] = useState('mixed');
    const [targetReachAddress, setTargetReachAddress] = useState('');
    const [targetReachIsLocating, setTargetReachIsLocating] = useState(false);
    const targetReachAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const targetReachMapRef = useRef<google.maps.Map | null>(null);

    // Controlled modal support
    const isModalOpen = props.isOpen !== undefined ? props.isOpen : internalModalOpen;
    const setModalOpen = function(open: boolean) {
        if (props.onOpenChange) {
            props.onOpenChange(open);
        }
        setInternalModalOpen(open);
    };

    // Get the effective campaign type
    const effectiveCampaignType = type;

    // Determine total steps based on campaign type
    // GeoFence and DrawRoutes campaigns have 6 steps (Type, Name, Org, Ads, Location/Route, Slots)
    // Other campaigns have 5 steps (Type, Name, Org, Ads, Slots)
    // TargetReach campaigns have 6 steps (Type, Name, Org, Ad, Config, Review) — unchanged
    const isGeoFenceCampaign = effectiveCampaignType === 'GeoFenceCampaign';
    const isDrawRoutesCampaign = effectiveCampaignType === 'DrawRoutesCampaign';
    const isTargetReachCampaign = effectiveCampaignType === 'TargetReachCampaign';
    const totalSteps = isTargetReachCampaign ? 6 : (isGeoFenceCampaign || isDrawRoutesCampaign) ? 6 : 5;

    // Generate a unique campaign name that doesn't conflict with existing campaigns
    const generateUniqueName = function(existingCampaigns: any[]) {
        const existingNames = existingCampaigns.map(function(campaign) {
            return campaign.name ? campaign.name.toLowerCase() : '';
        });
        var counter = 1;
        var candidateName = 'My Campaign ' + counter;

        while (existingNames.indexOf(candidateName.toLowerCase()) !== -1) {
            counter++;
            candidateName = 'My Campaign ' + counter;
        }

        return candidateName;
    };

    useEffect(() => {
        if (isModalOpen) {
            // Set campaign type from props if provided
            if (props.initialCampaignType) {
                setType(props.initialCampaignType);
            }

            // Generate unique name
            var uniqueName = generateUniqueName(props.existingCampaigns || []);
            setName(uniqueName);

            // Select the text after a short delay to ensure input is rendered
            setTimeout(function() {
                if (nameInputRef.current) {
                    nameInputRef.current.select();
                }
            }, 100);

            getOrganizations().then(profile => {
                if (profile && profile.organizations) {
                    setOrganizations(profile.organizations);
                    if (profile.organizations.length === 1) {
                        setOrgId(profile.organizations[0].id);
                    }
                }
            });
        }
    }, [isModalOpen, props.initialCampaignType]);

    // Load ads when organization changes
    useEffect(() => {
        if (org_id) {
            getAdsByOrganizationId(org_id).then(result => {
                setAds(result || []);
                setSelectedAdIds([]); // Reset ad selection when org changes
            });
        }
    }, [org_id]);

    const resetForm = () => {
        setName('');
        setType('');
        setSelectedAdIds([]);
        setOrgId('');
        setCurrentStep(1);
        setIsSubmitting(false);
        setIsComplete(false);
        setGeoFenceLocation(null);
        setShowGeoFenceModal(false);
        setDrawRoutesConfig(null);
        setShowDrawRouteModal(false);
        setError(null);
        // Reset activation state
        setCreatedCampaignId(null);
        // Reset target reach state
        setTargetReachUserPrompt('');
        setTargetReachStartLat('32.7157');
        setTargetReachStartLng('-117.1611');
        setTargetReachMaxHours('4');
        setTargetReachBudgetDays('14');
        setTargetReachRoadPref('mixed');
        setTargetReachAddress('');
    };

    // Target Reach map handlers
    const handleTargetReachMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            if (!isWithinSoCalBounds(lat, lng)) return;
            setTargetReachStartLat(lat.toFixed(4));
            setTargetReachStartLng(lng.toFixed(4));

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    setTargetReachAddress(results[0].formatted_address);
                } else {
                    setTargetReachAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
                }
            });
        }
    }, []);

    const handleTargetReachPlaceChanged = useCallback(() => {
        if (targetReachAutocompleteRef.current) {
            const place = targetReachAutocompleteRef.current.getPlace();
            if (place.geometry && place.geometry.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                setTargetReachStartLat(lat.toFixed(4));
                setTargetReachStartLng(lng.toFixed(4));
                setTargetReachAddress(place.formatted_address || place.name || '');
                if (targetReachMapRef.current) {
                    targetReachMapRef.current.panTo({ lat, lng });
                }
            }
        }
    }, []);

    const handleTargetReachAddressSearch = useCallback((searchAddress: string) => {
        if (!searchAddress.trim()) return;
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: searchAddress }, (results, status) => {
            if (status === 'OK' && results && results[0] && results[0].geometry) {
                const location = results[0].geometry.location;
                const lat = location.lat();
                const lng = location.lng();
                const formattedAddress = results[0].formatted_address || searchAddress;
                setTargetReachStartLat(lat.toFixed(4));
                setTargetReachStartLng(lng.toFixed(4));
                setTargetReachAddress(formattedAddress);
                if (targetReachMapRef.current) {
                    targetReachMapRef.current.panTo({ lat, lng });
                    targetReachMapRef.current.setZoom(14);
                }
            }
        });
    }, []);

    const handleTargetReachCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) return;
        setTargetReachIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                if (!isWithinSoCalBounds(lat, lng)) {
                    console.warn('Current location is outside Southern California');
                    setTargetReachIsLocating(false);
                    return;
                }
                setTargetReachStartLat(lat.toFixed(4));
                setTargetReachStartLng(lng.toFixed(4));

                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    const addr = (status === 'OK' && results && results[0])
                        ? results[0].formatted_address
                        : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                    setTargetReachAddress(addr);
                    if (targetReachMapRef.current) {
                        targetReachMapRef.current.panTo({ lat, lng });
                        targetReachMapRef.current.setZoom(14);
                    }
                    setTargetReachIsLocating(false);
                });
            },
            () => { setTargetReachIsLocating(false); },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, []);

    const handleModalToggle = () => {
        if (isModalOpen) {
            resetForm();
            props.triggerRefresh(true);
        }
        setModalOpen(!isModalOpen);
    };

    const handleNext = async () => {
        // TargetReach: review step is the last step - create and navigate
        if (isTargetReachCampaign && currentStep === totalSteps) {
            setError(null);
            setIsSubmitting(true);
            try {
                const result = await createTargetReachCampaign({
                    adId: Number(selectedAdIds[0]),
                    name: name,
                    userPrompt: targetReachUserPrompt,
                    startLat: Number(targetReachStartLat),
                    startLng: Number(targetReachStartLng),
                    maxDrivingHoursPerDay: Number(targetReachMaxHours),
                    budgetDays: Number(targetReachBudgetDays),
                    roadPreference: targetReachRoadPref
                });
                const campaignId = result.campaignId || result.id;
                props.triggerRefresh(new Date());
                handleModalToggle();
                if (props.onCampaignCreated && campaignId) {
                    props.onCampaignCreated(campaignId);
                }
            } catch (err: any) {
                console.error('Failed to create target reach campaign:', err);
                setError('Unable to create campaign. Please check your details and try again.');
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        // Review step (second to last) - create DRAFT, then move to activation step
        // (skip for TargetReach — it has no activation step; creation handled above)
        if (!isTargetReachCampaign && currentStep === totalSteps - 1) {
            setError(null);
            setIsSubmitting(true);
            try {
                const campaignConfig: any = {};

                // Include geo fence data for GeoFence campaigns
                if (isGeoFenceCampaign && geoFenceLocation) {
                    campaignConfig.geoFence = geoFenceLocation;
                }

                // Include draw routes data for DrawRoutes campaigns
                if (isDrawRoutesCampaign && drawRoutesConfig) {
                    campaignConfig.drawRoutes = drawRoutesConfig;
                }

                // Create campaign as DRAFT
                const result = await createCampaign({
                    "name": name,
                    "type": effectiveCampaignType,
                    "ads": selectedAdIds,
                    "config": campaignConfig
                });

                // Store the campaign ID
                const campaignId = result.campaignId || result.id;
                setCreatedCampaignId(campaignId);

                // Move to activation step
                setCurrentStep(currentStep + 1);
            } catch (err: any) {
                console.error('Failed to create campaign:', err);
                setError('Unable to create campaign. Please check your details and try again.');
                setIsSubmitting(false);
                return;
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        // Last step (Schedule Runs) - handled by ScheduleRunsStep's own Book button
        if (currentStep === totalSteps) {
            return;
        }

        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleActivated = () => {
        setIsComplete(true);
        props.triggerRefresh(new Date());
        setTimeout(() => {
            handleModalToggle();
        }, 1500);
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Step order for GeoFence: 1=Type, 2=Name, 3=Org, 4=Ads, 5=GeoFence, 6=Slots
    // Step order for others: 1=Type, 2=Name, 3=Org, 4=Ads, 5=Slots
    const getActualStep = function(displayStep: number): number {
        return displayStep;
    };

    const canProceed = () => {
        const actualStep = getActualStep(currentStep);

        if (isGeoFenceCampaign) {
            // GeoFence campaign step logic (6 steps)
            switch (actualStep) {
                case 1:
                    return true; // type is already set to GeoFenceCampaign
                case 2:
                    return name.trim() !== '';
                case 3:
                    return org_id !== '';
                case 4:
                    return selectedAdIds.length > 0;
                case 5:
                    return geoFenceLocation !== null; // Geo fence is required for GeoFence campaigns
                case 6:
                    return false; // Schedule Runs — button handled separately by ScheduleRunsStep
                default:
                    return false;
            }
        } else if (isDrawRoutesCampaign) {
            // DrawRoutes campaign step logic (6 steps)
            switch (actualStep) {
                case 1:
                    return true; // type is already set to DrawRoutesCampaign
                case 2:
                    return name.trim() !== '';
                case 3:
                    return org_id !== '';
                case 4:
                    return selectedAdIds.length > 0;
                case 5:
                    return drawRoutesConfig !== null && drawRoutesConfig.waypoints.length >= 2; // Route is required with at least 2 waypoints
                case 6:
                    return false; // Schedule Runs — button handled separately by ScheduleRunsStep
                default:
                    return false;
            }
        } else if (isTargetReachCampaign) {
            // TargetReach campaign step logic (6 steps, no schedule/slots)
            switch (actualStep) {
                case 1:
                    return true;
                case 2:
                    return name.trim() !== '';
                case 3:
                    return org_id !== '';
                case 4:
                    return selectedAdIds.length > 0;
                case 5:
                    return targetReachUserPrompt.trim() !== '' && !isNaN(Number(targetReachStartLat)) && !isNaN(Number(targetReachStartLng));
                case 6:
                    return true; // Review
                default:
                    return false;
            }
        } else {
            // Non-GeoFence/DrawRoutes campaign step logic (5 steps)
            switch (actualStep) {
                case 1:
                    return effectiveCampaignType !== '';
                case 2:
                    return name.trim() !== '';
                case 3:
                    return org_id !== '';
                case 4:
                    return selectedAdIds.length > 0;
                case 5:
                    return false; // Schedule Runs — button handled separately by ScheduleRunsStep
                default:
                    return false;
            }
        }
    };

    const getStepTitle = () => {
        const actualStep = getActualStep(currentStep);

        if (isGeoFenceCampaign) {
            switch (actualStep) {
                case 1: return 'Campaign Type';
                case 2: return 'Name Your Campaign';
                case 3: return 'Choose Organization';
                case 4: return 'Select Ads';
                case 5: return 'Geo Fence Location';
                case 6: return 'Schedule Runs';
                default: return '';
            }
        } else if (isDrawRoutesCampaign) {
            switch (actualStep) {
                case 1: return 'Campaign Type';
                case 2: return 'Name Your Campaign';
                case 3: return 'Choose Organization';
                case 4: return 'Select Ads';
                case 5: return 'Draw Your Route';
                case 6: return 'Schedule Runs';
                default: return '';
            }
        } else if (isTargetReachCampaign) {
            switch (actualStep) {
                case 1: return 'Campaign Type';
                case 2: return 'Name Your Campaign';
                case 3: return 'Choose Organization';
                case 4: return 'Select Ad';
                case 5: return 'Target Reach Config';
                case 6: return 'Review';
                default: return '';
            }
        } else {
            switch (actualStep) {
                case 1: return 'Campaign Type';
                case 2: return 'Name Your Campaign';
                case 3: return 'Choose Organization';
                case 4: return 'Select Ads';
                case 5: return 'Schedule Runs';
                default: return '';
            }
        }
    };

    const styles = {
        body: { padding: '32px 32px 16px', minHeight: '320px' } as React.CSSProperties,
        headerBlock: { textAlign: 'center' as const, marginBottom: '24px' },
        title: { color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' },
        subtitle: { color: colors.subtle, fontSize: '14px', margin: 0 },
        stepIndicator: { display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '18px' },
        stepDot: { width: '8px', height: '8px', borderRadius: '50%', background: colors.border, transition: 'all 0.3s' },
        stepDotActive: { width: '24px', height: '8px', borderRadius: '4px', background: PURPLE_GRADIENT, transition: 'all 0.3s' } as React.CSSProperties,
        stepDotCompleted: { width: '8px', height: '8px', borderRadius: '50%', background: colors.accent, transition: 'all 0.3s' },
        textInput: {
            backgroundColor: '#ffffff',
            border: `1px solid ${colors.border}`,
            borderRadius: radii.input,
            padding: '12px 14px',
            fontSize: '15px',
            width: '100%',
            outline: 'none',
            color: colors.body,
            transition: 'border-color 0.15s, box-shadow 0.15s'
        } as React.CSSProperties,
        footer: { padding: '16px 32px 24px', display: 'flex', justifyContent: 'space-between', gap: '12px', borderTop: `1px solid ${colors.border}` },
        primaryButton: {
            background: PURPLE_GRADIENT,
            border: 'none',
            padding: '13px 24px',
            fontSize: 15,
            fontWeight: 600,
            borderRadius: radii.button,
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
            color: '#ffffff'
        } as React.CSSProperties,
        card: {
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '14px',
            marginBottom: '10px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: '#ffffff'
        } as React.CSSProperties,
        cardSelected: {
            border: `1px solid ${colors.accent}`,
            borderRadius: '12px',
            padding: '14px',
            marginBottom: '10px',
            cursor: 'pointer',
            background: '#f5f3ff',
            boxShadow: `0 0 0 3px ${colors.accentRing}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        } as React.CSSProperties,
        cardIcon: {
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            flexShrink: 0
        },
        cardTitle: { fontSize: '15px', fontWeight: 600, color: colors.title },
        cardSubtitle: { fontSize: '13px', color: colors.subtle },
        typeCard: {
            border: `1px solid ${colors.border}`,
            borderRadius: '14px',
            padding: '18px',
            marginBottom: '10px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            background: '#ffffff'
        } as React.CSSProperties,
        typeCardSelected: {
            border: `1px solid ${colors.accent}`,
            borderRadius: '14px',
            padding: '18px',
            marginBottom: '10px',
            cursor: 'pointer',
            background: '#f5f3ff',
            boxShadow: `0 0 0 3px ${colors.accentRing}`
        } as React.CSSProperties,
        typeHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' },
        reviewItem: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${colors.border}` },
        reviewLabel: { color: colors.subtle, fontSize: '14px' },
        reviewValue: { color: colors.title, fontSize: '14px', fontWeight: 600 },
        spinnerContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 16px',
            textAlign: 'center' as const
        },
        dateTimeRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }
    };

    const campaignTypes = [
        {
            id: 'GeoFenceCampaign',
            name: 'Geo Fence',
            description: 'Target users within a specific location radius.',
            icon: <MapMarkerIcon />
        },
        {
            id: 'DrawRoutesCampaign',
            name: 'Draw your Route',
            description: 'Draw a custom route on the map for targeted advertising.',
            icon: <RouteIcon />
        },
        {
            id: 'TargetReachCampaign',
            name: 'Target Customer Reach',
            description: 'Let us reach your audience',
            icon: <RouteIcon />
        }
    ];

    // Step 2: Name Input (actual step 2 in new order)
    const renderStep1 = () => (
        <Form>
            <FormGroup label="Campaign Name" isRequired fieldId="campaign-name">
                <TextInput
                    isRequired
                    type="text"
                    id="campaign-name"
                    placeholder="Enter a name for your campaign"
                    value={name}
                    onChange={(value) => setName(value)}
                    innerRef={nameInputRef}
                    style={styles.textInput}
                />
            </FormGroup>
            <p style={{color: '#718096', fontSize: '13px', marginTop: '12px'}}>
                Choose a descriptive name that helps you identify this campaign later.
            </p>
        </Form>
    );

    // Step 2: Choose Organization
    const renderStep2 = () => (
        <div>
            <p style={{color: '#718096', fontSize: '14px', marginBottom: '16px'}}>
                Select the organization this campaign belongs to:
            </p>
            {organizations.length === 0 ? (
                <div style={styles.spinnerContainer}>
                    <Spinner size="lg" />
                    <p style={{color: '#718096', marginTop: '12px'}}>Loading organizations...</p>
                </div>
            ) : (
                organizations.map(org => (
                    <div
                        key={org.id}
                        style={org_id === org.id ? styles.cardSelected : styles.card}
                        onClick={() => setOrgId(org.id)}
                    >
                        <div style={styles.cardIcon}>
                            <BuildingIcon />
                        </div>
                        <div style={{flex: 1}}>
                            <div style={styles.cardTitle}>{org.organizationName}</div>
                            {org.address && org.address.city && (
                                <div style={styles.cardSubtitle}>{org.address.city}</div>
                            )}
                        </div>
                        {org_id === org.id && (
                            <CheckCircleIcon style={{color: '#667eea', fontSize: '20px'}} />
                        )}
                    </div>
                ))
            )}
        </div>
    );

    // Step 1: Campaign Type (card-based selection)
    const renderStepCampaignType = () => {
        const typeCardStyle = {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '20px',
            border: '2px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            textAlign: 'center' as const,
            flex: 1,
            minWidth: '140px'
        };
        const typeCardSelectedReach = {
            ...typeCardStyle,
            borderColor: '#8b5cf6',
            background: '#f5f3ff'
        };
        const typeCardSelectedGeo = {
            ...typeCardStyle,
            borderColor: '#10b981',
            background: '#ecfdf5'
        };
        const typeCardSelectedDrawRoutes = {
            ...typeCardStyle,
            borderColor: '#3b82f6',
            background: '#eff6ff'
        };
        const iconContainerStyle = {
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px',
            fontSize: '22px'
        };

        const typeCardSelectedTargetReach = {
            ...typeCardStyle,
            borderColor: '#ec4899',
            background: '#fdf2f8'
        };

        const getCardStyle = function(typeId: string) {
            if (type !== typeId) return typeCardStyle;
            switch (typeId) {
                case 'ProjectedReachCampaign': return typeCardSelectedReach;
                case 'GeoFenceCampaign': return typeCardSelectedGeo;
                case 'DrawRoutesCampaign': return typeCardSelectedDrawRoutes;
                case 'TargetReachCampaign': return typeCardSelectedTargetReach;
                default: return typeCardStyle;
            }
        };

        const getIconBg = function(_typeId: string) {
            return PURPLE_GRADIENT;
        };

        return (
            <div>
                <p style={{color: '#718096', fontSize: '14px', marginBottom: '20px', textAlign: 'center'}}>
                    What type of campaign would you like to create?
                </p>
                <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap' as const}}>
                    {campaignTypes.map(function(campaignType) {
                        const cost = creditCostForType(campaignType.id);
                        const pillStyle: React.CSSProperties = {
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            background: cost === 2 ? '#fef3c7' : '#e0e7ff',
                            color: cost === 2 ? '#92400e' : '#3730a3',
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: '999px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                        };
                        return (
                            <div
                                key={campaignType.id}
                                style={{...getCardStyle(campaignType.id), position: 'relative'}}
                                onClick={function() { setType(campaignType.id); }}
                            >
                                <span style={pillStyle}>Uses {cost} credit{cost === 1 ? '' : 's'}</span>
                                <div style={{...iconContainerStyle, background: getIconBg(campaignType.id), color: '#ffffff'}}>
                                    {campaignType.icon}
                                </div>
                                <h4 style={{margin: '0 0 6px 0', fontSize: '14px', fontWeight: 600, color: '#1f2937'}}>
                                    {campaignType.name}
                                </h4>
                                <p style={{margin: 0, fontSize: '12px', color: '#6b7280', lineHeight: 1.4}}>
                                    {campaignType.description}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Step 4: Choose Ads
    const toggleAdSelection = function(adId: string) {
        if (isTargetReachCampaign) {
            // Single-select for TargetReach (API takes adId, not ads[])
            setSelectedAdIds(function(prev) {
                return prev.indexOf(adId) !== -1 ? [] : [adId];
            });
        } else {
            setSelectedAdIds(function(prev) {
                if (prev.indexOf(adId) !== -1) {
                    return prev.filter(function(id) { return id !== adId; });
                } else {
                    return prev.concat([adId]);
                }
            });
        }
    };

    const renderStep4 = () => (
        <div>
            <p style={{color: '#718096', fontSize: '14px', marginBottom: '16px'}}>
                {isTargetReachCampaign ? 'Select the ad for this campaign:' : 'Select one or more ads to include in this campaign:'}
            </p>
            {selectedAdIds.length > 0 && (
                <p style={{color: '#667eea', fontSize: '13px', marginBottom: '12px', fontWeight: 500}}>
                    {selectedAdIds.length} ad{selectedAdIds.length !== 1 ? 's' : ''} selected
                </p>
            )}
            {ads.length === 0 ? (
                <div style={styles.spinnerContainer}>
                    <p style={{color: '#718096', fontSize: '14px'}}>
                        No ads found for this organization. Please create an ad first and make sure it has media.
                    </p>
                </div>
            ) : (
                ads.map(function(ad) {
                    var isSelected = selectedAdIds.indexOf(ad.adId) !== -1;
                    return (
                        <div
                            key={ad.adId}
                            style={isSelected ? styles.cardSelected : styles.card}
                            onClick={function() { toggleAdSelection(ad.adId); }}
                        >
                            <div style={styles.cardIcon}>
                                <CubesIcon />
                            </div>
                            <div style={{flex: 1}}>
                                <div style={styles.cardTitle}>{ad.name}</div>
                                <div style={styles.cardSubtitle}>{ad.organizationName}</div>
                            </div>
                            {isSelected && (
                                <CheckCircleIcon style={{color: '#667eea', fontSize: '20px'}} />
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );

    // Step: Geo Fence Location (only for GeoFence campaigns)
    const renderStepGeoFence = () => (
        <div>
            <p style={{color: '#718096', fontSize: '14px', marginBottom: '16px'}}>
                Select the location for your geo fence:
            </p>

            {geoFenceLocation ? (
                <div
                    style={{
                        border: '2px solid #10b981',
                        borderRadius: '12px',
                        padding: '20px',
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
                        cursor: 'pointer'
                    }}
                    onClick={() => setShowGeoFenceModal(true)}
                >
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px'}}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff'
                        }}>
                            <MapMarkerIcon />
                        </div>
                        <div style={{flex: 1}}>
                            <div style={{fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '4px'}}>
                                {geoFenceLocation.address}
                            </div>
                            <div style={{fontSize: '13px', color: '#6b7280'}}>
                                Radius: {geoFenceLocation.radius}m
                            </div>
                        </div>
                        <CheckCircleIcon style={{color: '#10b981', fontSize: '24px'}} />
                    </div>
                    <div style={{fontSize: '12px', color: '#10b981', fontWeight: 500}}>
                        Click to edit location
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '12px',
                        padding: '40px 20px',
                        textAlign: 'center' as const,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                    onClick={() => setShowGeoFenceModal(true)}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '24px',
                        margin: '0 auto 16px'
                    }}>
                        <MapMarkerIcon />
                    </div>
                    <div style={{fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px'}}>
                        Select Location
                    </div>
                    <div style={{fontSize: '13px', color: '#6b7280'}}>
                        Click to open the map and choose your geo fence location
                    </div>
                </div>
            )}

            <GeoFenceMapModal
                isOpen={showGeoFenceModal}
                onClose={() => setShowGeoFenceModal(false)}
                onConfirm={(location) => {
                    setGeoFenceLocation(location);
                    setShowGeoFenceModal(false);
                }}
                initialLocation={geoFenceLocation || undefined}
            />
        </div>
    );

    // Step: Draw Routes (only for DrawRoutes campaigns)
    const renderStepDrawRoutes = () => (
        <div>
            <p style={{color: '#718096', fontSize: '14px', marginBottom: '16px'}}>
                Draw your custom route on the map:
            </p>

            {drawRoutesConfig ? (
                <div
                    style={{
                        border: '2px solid #3b82f6',
                        borderRadius: '12px',
                        padding: '20px',
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(29, 78, 216, 0.05) 100%)',
                        cursor: 'pointer'
                    }}
                    onClick={() => setShowDrawRouteModal(true)}
                >
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px'}}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff'
                        }}>
                            <RouteIcon />
                        </div>
                        <div style={{flex: 1}}>
                            <div style={{fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '4px'}}>
                                {drawRoutesConfig.startingPoint.address}
                            </div>
                            <div style={{fontSize: '13px', color: '#6b7280'}}>
                                {drawRoutesConfig.waypoints.length} waypoints
                                {drawRoutesConfig.totalDistanceText && ` \u2022 ${drawRoutesConfig.totalDistanceText}`}
                            </div>
                        </div>
                        <CheckCircleIcon style={{color: '#3b82f6', fontSize: '24px'}} />
                    </div>
                    <div style={{fontSize: '12px', color: '#3b82f6', fontWeight: 500}}>
                        Click to edit route
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '12px',
                        padding: '40px 20px',
                        textAlign: 'center' as const,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                    onClick={() => setShowDrawRouteModal(true)}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '24px',
                        margin: '0 auto 16px'
                    }}>
                        <RouteIcon />
                    </div>
                    <div style={{fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px'}}>
                        Draw Your Route
                    </div>
                    <div style={{fontSize: '13px', color: '#6b7280'}}>
                        Click to open the map and draw your custom route
                    </div>
                </div>
            )}

            <DrawRouteMapModal
                isOpen={showDrawRouteModal}
                onClose={() => setShowDrawRouteModal(false)}
                onConfirm={(config) => {
                    setDrawRoutesConfig(config);
                    setShowDrawRouteModal(false);
                }}
                initialConfig={drawRoutesConfig || undefined}
            />
        </div>
    );

    // Step: Target Reach Config (only for TargetReach campaigns)
    const renderStepTargetReachConfig = () => {
        const roadPrefOptions = [
            { id: 'highways', label: 'Highways', description: 'Prefer highways and freeways' },
            { id: 'local', label: 'Local Roads', description: 'Prefer local and residential roads' },
            { id: 'mixed', label: 'Mixed', description: 'Balance of highways and local roads' }
        ];

        const cardStyle = (isSelected: boolean): React.CSSProperties => ({
            flex: 1,
            padding: '14px',
            borderRadius: '10px',
            border: `2px solid ${isSelected ? '#ec4899' : '#e5e7eb'}`,
            background: isSelected ? '#fdf2f8' : '#ffffff',
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'all 0.2s ease'
        });

        return (
            <div>
                <p style={{color: '#718096', fontSize: '14px', marginBottom: '20px'}}>
                    Describe your target audience and configure route parameters:
                </p>

                {/* User Prompt */}
                <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px'}}>
                        Target Audience
                    </label>
                    <textarea
                        value={targetReachUserPrompt}
                        onChange={(e) => setTargetReachUserPrompt(e.target.value)}
                        placeholder="e.g., 500 families with kids in San Diego"
                        style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '12px 14px',
                            fontSize: '14px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '10px',
                            outline: 'none',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box'
                        }}
                    />
                </div>

                {/* Start Location */}
                <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px'}}>
                        Start Location
                    </label>

                    {!GOOGLE_MAPS_API_KEY ? (
                        <div style={{
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            fontSize: '13px',
                            color: '#dc2626'
                        }}>
                            <strong>Configuration Required:</strong> Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file and restart the development server.
                        </div>
                    ) : (
                        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                            {/* Address Search */}
                            <div style={{position: 'relative', marginBottom: '8px'}}>
                                <div style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    zIndex: 1,
                                    pointerEvents: 'none'
                                }}>
                                    <SearchIcon />
                                </div>
                                <Autocomplete
                                    onLoad={(autocomplete) => {
                                        targetReachAutocompleteRef.current = autocomplete;
                                        autocomplete.setFields(['formatted_address', 'geometry', 'name']);
                                        autocomplete.setBounds(SOCAL_AUTOCOMPLETE_BOUNDS);
                                        autocomplete.setOptions({ strictBounds: true });
                                    }}
                                    onPlaceChanged={handleTargetReachPlaceChanged}
                                >
                                    <input
                                        type="text"
                                        placeholder="Search for an address..."
                                        value={targetReachAddress}
                                        onChange={(e) => setTargetReachAddress(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleTargetReachAddressSearch(targetReachAddress);
                                            }
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px 10px 36px',
                                            fontSize: '14px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '10px',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </Autocomplete>
                            </div>

                            {/* Inline Map */}
                            <div style={{borderRadius: '10px', overflow: 'hidden', border: '2px solid #e2e8f0'}}>
                                <GoogleMap
                                    mapContainerStyle={{width: '100%', height: '250px'}}
                                    center={{lat: Number(targetReachStartLat), lng: Number(targetReachStartLng)}}
                                    zoom={12}
                                    onClick={handleTargetReachMapClick}
                                    onLoad={(mapInstance) => { targetReachMapRef.current = mapInstance; }}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                        restriction: SOCAL_RESTRICTION
                                    }}
                                >
                                    <Marker position={{lat: Number(targetReachStartLat), lng: Number(targetReachStartLng)}} />
                                </GoogleMap>
                            </div>

                            {/* Coordinates display & current location button */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '6px'
                            }}>
                                <span style={{fontSize: '12px', color: '#6b7280'}}>
                                    {targetReachStartLat}, {targetReachStartLng}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleTargetReachCurrentLocation}
                                    disabled={targetReachIsLocating}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '12px',
                                        color: targetReachIsLocating ? '#9ca3af' : '#667eea',
                                        background: 'none',
                                        border: 'none',
                                        cursor: targetReachIsLocating ? 'wait' : 'pointer',
                                        padding: '2px 0'
                                    }}
                                >
                                    <CrosshairsIcon /> {targetReachIsLocating ? 'Locating...' : 'Use my location'}
                                </button>
                            </div>
                        </LoadScript>
                    )}
                </div>

                {/* Max Hours & Budget Days */}
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px'}}>
                    <div>
                        <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px'}}>
                            Max Driving Hours/Day
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="12"
                            value={targetReachMaxHours}
                            onChange={(e) => setTargetReachMaxHours(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                fontSize: '14px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    <div>
                        <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px'}}>
                            Budget Days
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="90"
                            value={targetReachBudgetDays}
                            onChange={(e) => setTargetReachBudgetDays(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                fontSize: '14px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>

                {/* Road Preference */}
                <div>
                    <label style={{display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px'}}>
                        Road Preference
                    </label>
                    <div style={{display: 'flex', gap: '10px'}}>
                        {roadPrefOptions.map(function(opt) {
                            return (
                                <div
                                    key={opt.id}
                                    style={cardStyle(targetReachRoadPref === opt.id)}
                                    onClick={function() { setTargetReachRoadPref(opt.id); }}
                                >
                                    <div style={{fontSize: '14px', fontWeight: 600, color: '#1f2937', marginBottom: '4px'}}>
                                        {opt.label}
                                    </div>
                                    <div style={{fontSize: '11px', color: '#6b7280'}}>
                                        {opt.description}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Step: Review (now just displays details, creates DRAFT on Next)
    const renderStepReview = () => {
        const selectedOrg = organizations.find(o => o.id === org_id);
        const selectedAds = ads.filter(function(a) { return selectedAdIds.indexOf(a.adId) !== -1; });
        const selectedType = campaignTypes.find(t => t.id === effectiveCampaignType);

        return (
            <div>
                {isSubmitting ? (
                    <div style={styles.spinnerContainer}>
                        <Spinner size="xl" />
                        <p style={{color: '#718096', marginTop: '16px', fontSize: '16px'}}>
                            Creating your campaign...
                        </p>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div style={{
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginBottom: '16px',
                                color: '#dc2626',
                                fontSize: '14px'
                            }}>
                                <strong>Error:</strong> {error}
                            </div>
                        )}
                        <p style={{color: '#718096', fontSize: '14px', marginBottom: '20px'}}>
                            Review your campaign details:
                        </p>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Campaign Name</span>
                            <span style={styles.reviewValue}>{name}</span>
                        </div>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Organization</span>
                            <span style={styles.reviewValue}>
                                {selectedOrg ? selectedOrg.organizationName : org_id}
                            </span>
                        </div>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Campaign Type</span>
                            <span style={styles.reviewValue}>
                                {selectedType ? selectedType.name : effectiveCampaignType}
                            </span>
                        </div>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Selected Ad{selectedAds.length !== 1 ? 's' : ''}</span>
                            <span style={styles.reviewValue}>
                                {selectedAds.length > 0
                                    ? selectedAds.map(function(a) { return a.name; }).join(', ')
                                    : selectedAdIds.join(', ')}
                            </span>
                        </div>
                        {isGeoFenceCampaign && geoFenceLocation && (
                            <div style={styles.reviewItem}>
                                <span style={styles.reviewLabel}>Geo Fence Location</span>
                                <span style={styles.reviewValue}>
                                    {geoFenceLocation.address} ({geoFenceLocation.radius}m radius)
                                </span>
                            </div>
                        )}
                        {isDrawRoutesCampaign && drawRoutesConfig && (
                            <div style={styles.reviewItem}>
                                <span style={styles.reviewLabel}>Route</span>
                                <span style={styles.reviewValue}>
                                    {drawRoutesConfig.startingPoint.address} ({drawRoutesConfig.waypoints.length} waypoints{drawRoutesConfig.totalDistanceText ? `, ${drawRoutesConfig.totalDistanceText}` : ''})
                                </span>
                            </div>
                        )}
                        {isTargetReachCampaign && (
                            <>
                                <div style={styles.reviewItem}>
                                    <span style={styles.reviewLabel}>User Prompt</span>
                                    <span style={{...styles.reviewValue, maxWidth: '280px', textAlign: 'right' as const}}>{targetReachUserPrompt}</span>
                                </div>
                                <div style={styles.reviewItem}>
                                    <span style={styles.reviewLabel}>Start Location</span>
                                    <span style={{...styles.reviewValue, textAlign: 'right' as const}}>
                                        {targetReachAddress && <span style={{display: 'block'}}>{targetReachAddress}</span>}
                                        <span style={{fontSize: '12px', color: '#6b7280'}}>{targetReachStartLat}, {targetReachStartLng}</span>
                                    </span>
                                </div>
                                <div style={styles.reviewItem}>
                                    <span style={styles.reviewLabel}>Max Hours/Day</span>
                                    <span style={styles.reviewValue}>{targetReachMaxHours}</span>
                                </div>
                                <div style={styles.reviewItem}>
                                    <span style={styles.reviewLabel}>Budget Days</span>
                                    <span style={styles.reviewValue}>{targetReachBudgetDays}</span>
                                </div>
                                <div style={styles.reviewItem}>
                                    <span style={styles.reviewLabel}>Road Preference</span>
                                    <span style={styles.reviewValue}>{targetReachRoadPref === 'highways' ? 'Highways' : targetReachRoadPref === 'local' ? 'Local Roads' : 'Mixed'}</span>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Step: Schedule Runs (final step for non-TargetReach campaigns)
    const renderStepScheduleRuns = () => {
        if (!createdCampaignId) {
            return (
                <div style={styles.spinnerContainer}>
                    <Spinner size="lg" />
                </div>
            );
        }
        return (
            <ScheduleRunsStep
                campaignId={createdCampaignId}
                onBooked={handleActivated}
                onCloseModal={handleModalToggle}
            />
        );
    };

    const renderStepContent = () => {
        const actualStep = getActualStep(currentStep);

        if (isGeoFenceCampaign) {
            // GeoFence: 1=Type, 2=Name, 3=Org, 4=Ads, 5=GeoFence, 6=Slots
            switch (actualStep) {
                case 1: return renderStepCampaignType();
                case 2: return renderStep1();  // Name
                case 3: return renderStep2();  // Organization
                case 4: return renderStep4();  // Select Ads
                case 5: return renderStepGeoFence();  // Geo Fence Location
                case 6: return renderStepScheduleRuns();  // Slot picker
                default: return null;
            }
        } else if (isDrawRoutesCampaign) {
            // DrawRoutes: 1=Type, 2=Name, 3=Org, 4=Ads, 5=DrawRoutes, 6=Slots
            switch (actualStep) {
                case 1: return renderStepCampaignType();
                case 2: return renderStep1();  // Name
                case 3: return renderStep2();  // Organization
                case 4: return renderStep4();  // Select Ads
                case 5: return renderStepDrawRoutes();  // Draw Routes
                case 6: return renderStepScheduleRuns();  // Slot picker
                default: return null;
            }
        } else if (isTargetReachCampaign) {
            // TargetReach: 1=Type, 2=Name, 3=Org, 4=Ad, 5=Config, 6=Review
            switch (actualStep) {
                case 1: return renderStepCampaignType();
                case 2: return renderStep1();  // Name
                case 3: return renderStep2();  // Organization
                case 4: return renderStep4();  // Select Ad
                case 5: return renderStepTargetReachConfig();  // Target Reach Config
                case 6: return renderStepReview();  // Review
                default: return null;
            }
        } else {
            // Others: 1=Type, 2=Name, 3=Org, 4=Ads, 5=Slots
            switch (actualStep) {
                case 1: return renderStepCampaignType();
                case 2: return renderStep1();  // Name
                case 3: return renderStep2();  // Organization
                case 4: return renderStep4();  // Select Ads
                case 5: return renderStepScheduleRuns();  // Slot picker
                default: return null;
            }
        }
    };

    const getNextButtonText = () => {
        if (isTargetReachCampaign && currentStep === totalSteps) return 'Create Campaign';
        if (currentStep === totalSteps) return 'Done';  // Slots step - handled by ScheduleRunsStep
        if (currentStep === totalSteps - 1) return 'Create & Continue';  // last config step → creates DRAFT, then slot picker
        return 'Next';
    };

    // Check if we should show the footer (hide on last step since it has its own button)
    const shouldShowFooter = () => {
        if (isTargetReachCampaign) return !isComplete;
        return currentStep !== totalSteps && !isComplete;
    };

    return (
        <React.Fragment>
            {!props.hideButton && (
                <GradientButton onClick={handleModalToggle}>
                    Create New Campaign
                </GradientButton>
            )}
            <AppModal isOpen={isModalOpen} onClose={handleModalToggle} width="lg">
                <div style={styles.body}>
                    <div style={styles.headerBlock}>
                        <LogoBadge size={48} />
                        <h1 style={styles.title}>{getStepTitle()}</h1>
                        <p style={styles.subtitle}>Step {currentStep} of {totalSteps}</p>

                        <div style={styles.stepIndicator}>
                            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                                <div
                                    key={step}
                                    style={
                                        step === currentStep
                                            ? styles.stepDotActive
                                            : step < currentStep
                                                ? styles.stepDotCompleted
                                                : styles.stepDot
                                    }
                                />
                            ))}
                        </div>
                    </div>

                    {renderStepContent()}
                </div>

                {shouldShowFooter() && (
                    <div style={styles.footer}>
                        <GradientButton
                            appearance="secondary"
                            onClick={currentStep === 1 ? handleModalToggle : handleBack}
                            isDisabled={isSubmitting}
                        >
                            <ArrowLeftIcon style={{ marginRight: 8 }} />
                            {currentStep === 1 ? 'Cancel' : 'Back'}
                        </GradientButton>
                        <GradientButton
                            onClick={handleNext}
                            isDisabled={!canProceed() || isSubmitting}
                            isLoading={isSubmitting}
                        >
                            {getNextButtonText()}
                            {(isTargetReachCampaign ? currentStep < totalSteps : currentStep < totalSteps - 1) && !isSubmitting && (
                                <ArrowRightIcon style={{ marginLeft: 8 }} />
                            )}
                        </GradientButton>
                    </div>
                )}
            </AppModal>
        </React.Fragment>
    );
}

export { CreateCampaign };
